import { WorkspaceSwitcherItem } from "@components/navigation/WorkspaceSwitcherItem";
import { useSwitchToPersonalView } from "@hooks/useSwitchToPersonalView";
import { useOrganization } from "@api/organization.api";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useScope } from "@state/scopeContext.lib";
import { OrganizationMemberClientAccessSource } from "@common/types/organizationMemberClientAccess";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import {
    useOrganizationUserCache,
    ORGANIZATION_USER_CACHE_KEYS,
} from "@hooks/useOrganizationUserCache";
import { useWorkspacesList } from "@api/workspace.api";
import { WorkspaceSchema } from "@common/schemas/workspace.schema";
import { useIdCopy } from "@hooks/useIdCopy";
import { Menu } from "@design-system/Menu/Menu.lib";
import { Loader } from "@design-system/Loader/Loader";
import { useOptionalSideNavContext } from "@design-system/SideNav/SideNavProvider";
import {
    pickRecentWorkspaces,
    withRecentWorkspaceId,
} from "@components/navigation/pickRecentWorkspaces";

const MAX_RECENT_WORKSPACES = 3;

export const ClientSwitcher: FC = () => {
    const goToPersonalView = useSwitchToPersonalView();
    const { getCopyProps } = useIdCopy({ label: "Workspace" });

    const workspacesQuery = useWorkspacesList();
    const activeWorkspace = useActiveWorkspace();
    const { data: userOrganization } = useOrganization();
    const { changeActiveWorkspace } = useScope();

    const [cachedRecentIds, setCachedRecentIds] = useOrganizationUserCache<
        string[]
    >(ORGANIZATION_USER_CACHE_KEYS.RECENT_WORKSPACES_CACHE_KEY);

    // Local state to track recent workspace IDs (since cache hook doesn't trigger re-renders)
    const [recentWorkspaceIds, setRecentWorkspaceIds] = useState<string[]>(
        cachedRecentIds ?? [],
    );

    const hasAccessToOrganization = userOrganization !== null;
    const showPersonalViewItem = !hasAccessToOrganization;

    // With a side nav mounted, `useRecordRecentWorkspace` is the cache's only writer.
    const isSideNavMounted = useOptionalSideNavContext() !== null;

    useEffect(() => {
        if (!activeWorkspace?.id) {
            return;
        }
        setRecentWorkspaceIds((currentRecent) => {
            const updatedRecent = withRecentWorkspaceId({
                recentIds: currentRecent,
                workspaceId: activeWorkspace.id,
            });
            if (!isSideNavMounted) {
                setCachedRecentIds(updatedRecent);
            }
            return updatedRecent;
        });
    }, [activeWorkspace?.id, isSideNavMounted, setCachedRecentIds]);

    // Determine which workspaces to show
    const workspaces = workspacesQuery?.data;
    const showAllWorkspaces = useMemo(
        () =>
            workspaces?.length
                ? workspaces.length <= MAX_RECENT_WORKSPACES
                : false,
        [workspaces],
    );
    const workspacesToShow = useMemo<WorkspaceSchema[]>(() => {
        if (!workspaces) {
            return [];
        }

        if (showAllWorkspaces) {
            return workspaces;
        }
        return pickRecentWorkspaces({
            workspaces,
            recentIds: recentWorkspaceIds,
            limit: MAX_RECENT_WORKSPACES,
        });
    }, [showAllWorkspaces, workspaces, recentWorkspaceIds]);

    const handleWorkspaceClick = useCallback(
        (workspaceId: string) => {
            changeActiveWorkspace(
                workspaceId,
                OrganizationMemberClientAccessSource.SWITCHER,
            );
        },
        [changeActiveWorkspace],
    );

    if (workspacesQuery.isLoading) {
        return <Loader />;
    }

    if (workspacesQuery?.data?.length === 0) {
        return null;
    }

    return (
        <>
            <Menu.RadioGroup
                value={activeWorkspace?.id ?? ""}
                onValueChange={handleWorkspaceClick}
            >
                <Menu.GroupLabel>Workspace</Menu.GroupLabel>
                {workspacesToShow.map((workspace) => (
                    <Menu.RadioItem key={workspace.id} value={workspace.id}>
                        <span {...getCopyProps(workspace.id)}>
                            {workspace.name}
                        </span>
                    </Menu.RadioItem>
                ))}
            </Menu.RadioGroup>
            {!showAllWorkspaces && (
                <>
                    <WorkspaceSwitcherItem />
                    {showPersonalViewItem && (
                        <Menu.Item onClick={goToPersonalView}>
                            View all workspaces
                        </Menu.Item>
                    )}
                </>
            )}
            <Menu.Separator />
        </>
    );
};
